/**
 * Token-aware fetch for the admin UI.
 *
 * Acquires the signed-in account's MSAL **ID token** silently and attaches it as
 * a Bearer header. The ID token's audience is our own app registration, so the
 * backend can cryptographically verify it (signature/issuer/audience/expiry) —
 * unlike the Microsoft Graph access token, which third parties cannot validate.
 *
 * Used for every `/api/*` call the backend gates with `requireUser` (booking-links
 * CRUD, service-mappings, campaigns, booking-businesses, lead-erp-options,
 * settings, security-groups).
 *
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @param {object} account - MSAL account (accounts[0])
 * @param {string} path    - e.g. "/api/booking-links" or "/api/service-mappings?linkId=…"
 * @param {RequestInit} [init]
 */
import { loginRequest } from "./authConfig.js";

export async function authedFetch(instance, account, path, init = {}) {
  const token = await instance.acquireTokenSilent({ ...loginRequest, account });
  return fetch(path, {
    ...init,
    headers: {
      ...(init.headers || {}),
      // Azure Static Web Apps RESERVES and overwrites the `Authorization` header
      // (it injects its own internal Function-invocation token), so the ID token
      // must ride in a custom header that SWA forwards untouched. `Authorization`
      // is kept for local dev / direct API access where there's no SWA proxy.
      "X-Booking-Auth": `Bearer ${token.idToken}`,
      Authorization: `Bearer ${token.idToken}`,
      "Content-Type": "application/json",
    },
  });
}
